import React, { ReactNode, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useUser, useWorkspace } from "@/hooks";
import { FeedbackContext } from "./feedback.context";

export const SentryFeedbackProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
    const { user } = useUser();
    const { workspace, userRoleNames } = useWorkspace();

    const openFeedbackAsync = useCallback(async () => {
        const feedback = Sentry.getFeedback();

        Sentry.setUser({
            fullName: user?.fullName,
            email: user?.email,
        });

        const tags = {
            user_organization_name: workspace?.organizationName,
            user_brand_name: workspace?.workspaceName,
            user_role_names: userRoleNames?.join(", "),
        };

        const form = await feedback?.createForm({
            id: `feedback-form`,
            autoInject: false,
            showBranding: false,
            formTitle: "Help us shine!",
            useSentryUser: {
                email: "email",
                name: "fullName",
            },
            tags,
            colorScheme: "light",
            submitButtonLabel: "Send Feedback",
            messagePlaceholder: "Leave us your comments",
            showName: false,
            showEmail: false,
            triggerLabel: "Feedback",
            triggerAriaLabel: "Feedback",

            onFormOpen: () => { },
            onFormClose: () => {
                form?.removeFromDom();
            },
            onSubmitSuccess: (data: any, eventID: any) => {
                console.log("Feedback submitted", data, eventID);
            },
            onSubmitError: (error: any) => {
                console.log("Feedback submission failed", error);
            },
        });
        form?.appendToDom();
        form?.open();

        // add react-aria-top-layer
        const feedbackFormContainer = document.getElementById("feedback-form");
        if (feedbackFormContainer) {
            feedbackFormContainer.dataset.reactAriaTopLayer = "true";
        }

    }, [user, workspace, userRoleNames]);

    const openFeedback = useCallback(() => {
        void openFeedbackAsync();
    }, [openFeedbackAsync]);

    const contextValue = useMemo(() => ({ openFeedback }), [openFeedback]);

    return (
        <FeedbackContext.Provider value={contextValue}>
            {children}
        </FeedbackContext.Provider>
    );
};
